import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Loading from 'components/global/Loading';
import Menu from 'components/global/Menu';
import Typography from 'components/global/Typography';
import SmsFooter from 'components/templates/SmsFooter';

import useChangeLanguage from 'hooks/useChangeLanguage';

import { openAxios } from 'services/axios';
import { OTO_LINK } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { languages } from 'utils/localization';

import { faAngleDown, faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

import * as S from './OTOLinkBuyer.styled';
import OTOLinkError, { ERROR_TYPES } from './OTOLinkError';
import OrderTracking from './OrderTracking';
import PaymentSuccessful from './PaymentSuccessful';
import ConfirmOrderDetailsTab from './Tabs/ConfirmOrderDetailsTab';
import ContactDetailsTab from './Tabs/ContactDetailsTab';
import OrderSummary from './Tabs/OrderSummary';
import PaymentTab from './Tabs/PaymentTab';
import SelectPaymentMethodTab from './Tabs/SelectPaymentMethodTab';
import SelectShippingMethodTab from './Tabs/SelectShippingMethodTab';

const STEP_STATUS = {
	new: 'new',
	contactFilled: 'contactFilled',
	shippingSelected: 'shippingSelected',
	checkoutCompleted: 'checkoutCompleted',
	paymentFailed: 'paymentFailed'
};

export const TAB_KEYS = {
	CONTACT_DETAILS: 'contactDetails',
	SELECT_PAYMENT_METHOD: 'selectPaymentMethod',
	SELECT_SHIPPING_METHOD: 'selectShippingMethod',
	CONFIRM_ORDER_DETAILS: 'confirmOrderDetails',
	PAYMENT: 'payment'
};

const TABS = [
	TAB_KEYS.CONTACT_DETAILS,
	TAB_KEYS.SELECT_PAYMENT_METHOD,
	TAB_KEYS.SELECT_SHIPPING_METHOD,
	TAB_KEYS.CONFIRM_ORDER_DETAILS,
	TAB_KEYS.PAYMENT
];

export const OTOLinkBuyer = () => {
	const changeLanguage = useChangeLanguage();
	const [searchParams] = useSearchParams();
	const localization = useSelector((state) => state.layout.localization);

	const [step, setStep] = useState(0);
	const [languageMenuButtonRef, setLanguageMenuButtonRef] = useState(null);
	const [loading, setLoading] = useState(true);
	const [paymentResult, setPaymentResult] = useState(null);
	const [isRenderResult, setIsRenderResult] = useState(false);
	const [showTracking, setShowTracking] = useState(false);
	const [errorCode, setErrorCode] = useState(null);

	useEffect(() => {
		changeLanguage('arabicValue');
	}, []);

	const contactDetailsSchema = useMemo(
		() =>
			yup.object().shape({
				firstName: yup.string().required(),
				lastName: yup.string().required(),
				phone: yup.string().required().phoneNumber(),
				email: yup.string().email().required(),
				address: yup.object().shape({
					text: yup.string().nullable().required(),
					countryName: yup.string().nullable().required(),
					stateName: yup.string().nullable(),
					cityName: yup.string().nullable().required(),
					districtName: yup.string().nullable(),
					street: yup.string().nullable(),
					buildingName: yup.string().nullable(),
					lat: yup.number().min(-90).max(90).nullable(),
					lng: yup.number().min(-180).max(180).nullable(),
					zipCode: yup.string().nullable(),
					addressInputType: yup.string().nullable()
				})
			}),
		[]
	);

	const selectPaymentSchema = useMemo(
		() =>
			yup.object().shape({
				paymentMethod: yup.string().oneOf(['CC', 'COD']).required()
			}),
		[]
	);

	const selectDCSchema = useMemo(
		() =>
			yup.object().shape({
				selectedDCSettingsId: yup.number().required(),
				shippingFee: yup.number().nullable()
			}),
		[]
	);

	const schema = useMemo(() => {
		switch (TABS[step]) {
			case TAB_KEYS.CONTACT_DETAILS:
				return contactDetailsSchema;
			case TAB_KEYS.SELECT_PAYMENT_METHOD:
				return selectPaymentSchema.concat(contactDetailsSchema);
			case TAB_KEYS.SELECT_SHIPPING_METHOD:
				return selectDCSchema.concat(selectPaymentSchema).concat(contactDetailsSchema);
			case TAB_KEYS.PAYMENT:
				return yup.object().shape({
					termsAndPrivacy: yup.bool().oneOf([true], 'fieldMustBeChecked')
				});
			default:
				return contactDetailsSchema;
		}
	}, [step]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			phone: null,
			shippingFee: null,
			paymentMethod: null,
			orderItems: [],
			deliveryCompanyList: [],
			selectedDCSettingsId: null,
			address: {
				text: null,
				countryName: null,
				stateName: null,
				cityName: null,
				districtName: null,
				street: null,
				buildingName: null,
				zipCode: null,
				lat: null,
				lng: null,
				addressInputType: null
			},
			termsAndPrivacy: false
		}
	});

	const { watch, reset } = formMethods;

	const getSessionData = async () => {
		try {
			setLoading(true);
			const key = searchParams.get('key');
			if (!key) {
				setErrorCode(ERROR_TYPES.KEY_NOT_FOUND);
				return;
			}

			const GUID = localStorage.getItem('GUID');
			const otoLinkSessionId = localStorage.getItem('otoLinkSessionId');

			const { data } = await openAxios.post(OTO_LINK.GET_OTO_LINK_BUYER, {
				key,
				GUID,
				otoLinkSessionId
			});
			if (data.status === 'inactive') {
				setErrorCode(ERROR_TYPES.INACTIVE_LINK);
				return;
			}
			reset({
				...watch(),
				companyLogo: data?.companyLogo,
				firstName: data?.sessionInfo?.firstName,
				lastName: data?.sessionInfo?.lastName,
				email: data?.sessionInfo?.email,
				phone: data?.sessionInfo?.mobileNumber || null,
				country: data.configCountry,
				address: {
					...data.sessionInfo?.address,
					text: data.sessionInfo?.address?.address,
					countryName: data.sessionInfo?.address?.country || data.configCountry,
					cityName: data.sessionInfo?.address?.city,
					stateName: data.sessionInfo?.address?.state,
					zipCode: data.sessionInfo?.address?.postalCode
				},
				orderItems: data?.items || [],
				currency: data.currency,
				grandTotal: data?.sessionInfo?.shippingTotal
					? data?.sessionInfo?.shippingTotal + data?.subTotal
					: data?.subTotal,
				subTotal: data?.subTotal,
				GUID: data?.GUID,
				otoLinkSessionId: data?.otoLinkSessionId || data?.sessionInfo?.otoLinkSessionId,
				key,
				paymentProvider: data?.paymentProvider,
				allowedPaymentMethods: data?.paymentMethod,
				shippingFee: data?.sessionInfo?.shippingTotal || null,
				selectedDCSettingsId: data?.sessionInfo?.selectedShippingOptionId, // DC settings id
				paymentMethod: data?.sessionInfo?.selectedPaymentMethod,
				trackingDetails: data?.sessionInfo?.trackingDetails
			});
			if (data?.sessionInfo?.status === STEP_STATUS.checkoutCompleted) {
				setPaymentResult({
					amount: data?.sessionInfo?.grandTotal || 0,
					orderId: data?.sessionInfo?.otoOrderID,
					orderDate: data?.sessionInfo?.deliveryDate,
					trackingUrl: data?.sessionInfo?.trackingURL
				});
				setIsRenderResult(true);
			}

			localStorage.setItem('GUID', data?.GUID);
			localStorage.setItem('otoLinkSessionId', data?.otoLinkSessionId || data?.sessionInfo?.otoLinkSessionId);
		} catch (error) {
			localStorage.removeItem('GUID');
			localStorage.removeItem('otoLinkSessionId');
			setErrorCode(error?.response?.data?.code || ERROR_TYPES.KEY_NOT_FOUND);
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getSessionData();
	}, []);

	const handleOnPaymentSuccess = async () => {
		await getSessionData();
	};

	const nextStep = () => {
		document.getElementById('form-header')?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
		if (step + 1 < TABS.length) setStep(step + 1);
	};

	const prevStep = () => {
		if (step - 1 >= 0) setStep(step - 1);
	};

	const goToStep = (tab) => {
		const index = TABS.indexOf(tab);
		if (index !== -1) setStep(index);
	};

	const renderTabContent = useMemo(() => {
		const tab = TABS[step];
		switch (tab) {
			case TAB_KEYS.CONTACT_DETAILS:
				return <ContactDetailsTab nextStep={nextStep} />;
			case TAB_KEYS.SELECT_PAYMENT_METHOD:
				return <SelectPaymentMethodTab nextStep={nextStep} prevStep={prevStep} />;
			case TAB_KEYS.SELECT_SHIPPING_METHOD:
				return (
					<SelectShippingMethodTab
						nextStep={nextStep}
						prevStep={prevStep}
						goToContactDetailsTab={() => goToStep(TAB_KEYS.CONTACT_DETAILS)}
					/>
				);
			case TAB_KEYS.CONFIRM_ORDER_DETAILS:
				return <ConfirmOrderDetailsTab goToStep={goToStep} onPaymentSuccess={handleOnPaymentSuccess} />;
			case TAB_KEYS.PAYMENT:
				return <PaymentTab nextStep={nextStep} prevStep={prevStep} onPaymentSuccess={handleOnPaymentSuccess} />;
			default:
				return null;
		}
	}, [step]);

	if (loading) {
		return <Loading />;
	}

	return (
		<S.Container>
			<S.NavBar>
				<S.NavBarContent>
					<Image
						width={'4.5rem'}
						height={'4.5rem'}
						url={
							watch('companyLogo')
								? watch('companyLogo')
								: 'https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png'
						}
					/>
					<Button variant="borderless" ref={setLanguageMenuButtonRef}>
						<Image url={localization.icon} width="20px" height="15px" />
						<Typography>{localization.name}</Typography>
						<Icon icon={faAngleDown} />
					</Button>
					<Menu
						buttonRef={languageMenuButtonRef}
						menuItems={languages.map((language) => ({
							label: language.name,
							img: language.icon,
							imgProps: { width: '20px', height: '15px' },
							selected: language.code === localization.code,
							action: () => {
								changeLanguage(language.code);
							}
						}))}
					/>
				</S.NavBarContent>
			</S.NavBar>

			<S.ScrollContainer>
				<FormProvider {...formMethods}>
					{isRenderResult ? (
						<>
							{showTracking ? (
								<OrderTracking {...watch('trackingDetails')} />
							) : (
								<PaymentSuccessful
									paymentMethod={watch('paymentMethod')}
									currency={watch('currency')}
									data={paymentResult}
									showTrackingOrder={() => setShowTracking(true)}
								/>
							)}
						</>
					) : errorCode ? (
						<OTOLinkError errorCode={errorCode} />
					) : (
						<S.Content>
							<S.ContentWrapper>{renderTabContent}</S.ContentWrapper>
							<OrderSummary />
						</S.Content>
					)}
				</FormProvider>

				<SmsFooter />
			</S.ScrollContainer>
		</S.Container>
	);
};

export const FormHeader = ({ title, onClick, className }) => {
	return (
		<S.FormHeader className={className} id="form-header">
			{onClick && (
				<Button
					icon={faArrowLeft}
					variant="transparent"
					color="black"
					size="x-small"
					iconSize="medium"
					iconButton
					onClick={onClick}
				/>
			)}
			<Typography variant="h4" noWrap fontWeight="700">
				{title}
			</Typography>
		</S.FormHeader>
	);
};

export default OTOLinkBuyer;
